<template>
  <main @click.capture="recordClick">
    <header><p class="eyebrow">RESEARCH · 참여자용</p><h1>{{ study.title }}</h1></header>
    <ParticipantEntry v-if="stage === 'entry'" :instructions="study.instructions" :busy="busy" @start="start" />
    <ReadingScreen v-else-if="stage === 'reading'" :material="material" :index="materialIndex" :total="study.materials.length" :busy="busy" @complete="completeReading" />
    <QuestionScreen v-else-if="stage === 'question'" :key="question.id" :material="material" :question="question" :index="questionIndex" :required="study.presentation.required" :busy="busy" :last="isLast" @submit="submitAnswer" @reference="recordReference" />
    <section v-else tabindex="-1"><h2>참여가 완료되었습니다</h2><p>모든 응답이 저장되었습니다. 참여해주셔서 감사합니다.</p></section>
    <p v-if="busy" role="status">응답을 저장하고 있습니다. 잠시 기다려주세요.</p>
    <p v-if="error" role="alert" class="error">{{ error }}</p>
    <footer>자료와 문항은 시연용 예시입니다. 실제 연구에 사용하기 전에 연구자가 내용을 교체해야 합니다.</footer>
  </main>
</template>

<script>
import ParticipantEntry from './components/ParticipantEntry.vue';
import ReadingScreen from './components/ReadingScreen.vue';
import QuestionScreen from './components/QuestionScreen.vue';
import { study } from './data/study';
import { openSession, saveCheckpoint, describeError } from './services/surveyRepository';

const LOG_LIMIT = 1500;
export default {
  components: { ParticipantEntry, ReadingScreen, QuestionScreen },
  data() {
    return {
      study, stage: 'entry', materialIndex: 0, questionIndex: 0,
      participantId: '', sessionId: '', revision: 0, sessionStartedAt: null, startedAt: null, stageStartedAt: null,
      busy: false, error: '', responses: {}, readingRecords: [], answerRecords: [],
      clickCount: 0, clickLog: [], behaviorLog: [], droppedClicks: 0, droppedBehaviors: 0,
      referenceOpenedAt: null
    };
  },
  computed: {
    material() { return this.study.materials[this.materialIndex]; },
    question() { return this.material.questions[this.questionIndex]; },
    isLast() { return this.materialIndex === this.study.materials.length - 1 && this.questionIndex === this.material.questions.length - 1; }
  },
  mounted() { window.addEventListener('beforeunload', this.beforeUnload); },
  beforeUnmount() { window.removeEventListener('beforeunload', this.beforeUnload); },
  methods: {
    beforeUnload(event) {
      if (this.stage !== 'entry' && this.stage !== 'complete') { event.preventDefault(); event.returnValue = ''; }
    },
    async start(id) {
      if (this.busy || !id.trim()) return;
      this.busy = true;
      this.error = '';
      try {
        const { sessionId, checkpoint } = await openSession();
        if (checkpoint && (checkpoint.participant_id !== id || checkpoint.study_id !== this.study.id || checkpoint.study_version !== this.study.version)) {
          this.error = '이 브라우저에 다른 참여자 ID 또는 연구 버전의 기록이 연결되어 있습니다. 기존 참여자 ID를 확인하거나 연구자에게 문의해주세요.';
          return;
        }
        this.sessionId = sessionId;
        this.participantId = id;
        if (checkpoint) {
          const progress = checkpoint.progress;
          const material = progress && this.study.materials[progress.material_index];
          if (!material || !['reading', 'question', 'complete'].includes(progress.stage) || !material.questions[progress.question_index] || !Number.isInteger(checkpoint.revision)) {
            this.error = '저장된 진행 기록을 확인할 수 없습니다. 연구자에게 문의해주세요.';
            return;
          }
          this.revision = checkpoint.revision;
          this.responses = checkpoint.responses;
          this.readingRecords = checkpoint.reading_records;
          this.answerRecords = checkpoint.answer_records;
          this.clickLog = checkpoint.click_log;
          this.behaviorLog = checkpoint.behavior_log;
          this.clickCount = checkpoint.total_clicks;
          this.droppedClicks = checkpoint.dropped_click_logs;
          this.droppedBehaviors = checkpoint.dropped_behavior_logs;
          this.sessionStartedAt = checkpoint.started_at;
          // 체크포인트 이후 이탈 시간은 전체 시간에 더하지 않습니다.
          this.startedAt = Date.now() - checkpoint.duration_seconds * 1000;
          this.enterStage(progress.stage, progress.material_index, progress.question_index);
        } else {
          this.revision = 0;
          this.startedAt = Date.now();
          this.sessionStartedAt = new Date(this.startedAt).toISOString();
          this.busy = false;
          await this.persist({ stage: 'reading', materialIndex: 0, questionIndex: 0 }, {});
        }
      } catch (error) {
        console.error('Session initialization failed:', error.code || error.name);
        this.error = describeError(error);
      } finally { this.busy = false; }
    },
    enterStage(stage, materialIndex, questionIndex) {
      this.stage = stage;
      this.materialIndex = materialIndex;
      this.questionIndex = questionIndex;
      this.stageStartedAt = Date.now();
      this.referenceOpenedAt = null;
      this.$nextTick(() => {
        const heading = this.$el.querySelector('section h2');
        if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus(); }
      });
    },
    context() {
      return { stage: this.stage, material_id: this.material.id, question_id: this.stage === 'question' ? this.question.id : null };
    },
    recordClick(event) {
      if (!this.startedAt || this.stage === 'entry' || this.stage === 'complete') return;
      this.clickCount++;
      if (this.clickLog.length >= LOG_LIMIT) { this.droppedClicks++; return; }
      this.clickLog.push({ x: event.clientX, y: event.clientY, time: (Date.now() - this.startedAt) / 1000, target: event.target.tagName, ...this.context() });
    },
    recordReference(expanded) {
      const now = Date.now();
      const record = {
        type: expanded ? 'reference_open' : 'reference_close',
        time: (now - this.startedAt) / 1000,
        ...this.context()
      };
      if (expanded) this.referenceOpenedAt = now;
      else {
        record.duration_seconds = this.referenceOpenedAt ? (now - this.referenceOpenedAt) / 1000 : 0;
        this.referenceOpenedAt = null;
      }
      if (this.behaviorLog.length < LOG_LIMIT) this.behaviorLog.push(record);
      else this.droppedBehaviors++;
    },
    async persist(next, updates) {
      if (this.busy) return false;
      this.busy = true;
      this.error = '';
      const now = Date.now();
      try {
        await saveCheckpoint(this.sessionId, {
          schema_version: 1, study_id: this.study.id, study_version: this.study.version,
          participant_id: this.participantId, presentation: this.study.presentation,
          started_at: this.sessionStartedAt,
          status: next.stage === 'complete' ? 'completed' : 'in_progress',
          progress: { stage: next.stage, material_index: next.materialIndex, question_index: next.questionIndex },
          responses: this.responses, reading_records: this.readingRecords, answer_records: this.answerRecords,
          duration_seconds: (now - this.startedAt) / 1000,
          total_clicks: this.clickCount, click_log: this.clickLog.map(item => ({ ...item })),
          behavior_log: this.behaviorLog.map(item => ({ ...item })),
          dropped_click_logs: this.droppedClicks, dropped_behavior_logs: this.droppedBehaviors,
          checkpoint_at: new Date(now).toISOString(), ...updates
        }, this.revision);
        this.revision++;
        if (updates.responses) this.responses = updates.responses;
        if (updates.reading_records) this.readingRecords = updates.reading_records;
        if (updates.answer_records) this.answerRecords = updates.answer_records;
        this.enterStage(next.stage, next.materialIndex, next.questionIndex);
        return true;
      } catch (error) {
        console.error('Checkpoint save failed:', error.code || error.name);
        this.error = `${describeError(error)} 응답은 현재 화면에 유지됩니다.`;
        return false;
      } finally { this.busy = false; }
    },
    completeReading() {
      if (this.busy) return;
      const now = Date.now();
      this.persist({ stage: 'question', materialIndex: this.materialIndex, questionIndex: 0 }, {
        reading_records: [...this.readingRecords, { material_id: this.material.id, started_at: new Date(this.stageStartedAt).toISOString(), completed_at: new Date(now).toISOString(), duration_seconds: (now - this.stageStartedAt) / 1000 }]
      });
    },
    submitAnswer(answer) {
      if (this.busy || (this.study.presentation.required && !answer.trim())) return;
      const now = Date.now();
      const next = this.isLast ? { stage: 'complete', materialIndex: this.materialIndex, questionIndex: this.questionIndex }
        : this.questionIndex + 1 < this.material.questions.length
          ? { stage: 'question', materialIndex: this.materialIndex, questionIndex: this.questionIndex + 1 }
          : { stage: 'reading', materialIndex: this.materialIndex + 1, questionIndex: 0 };
      this.persist(next, {
        responses: { ...this.responses, [`${this.material.id}/${this.question.id}`]: answer, ...(this.question.id === 'feedback' ? { feedback: answer } : {}) },
        answer_records: [...this.answerRecords, { material_id: this.material.id, question_id: this.question.id, response: answer, started_at: new Date(this.stageStartedAt).toISOString(), completed_at: new Date(now).toISOString(), duration_seconds: (now - this.stageStartedAt) / 1000, reference_open_at_submit: this.referenceOpenedAt !== null, reference_final_interval_seconds: this.referenceOpenedAt === null ? 0 : (now - this.referenceOpenedAt) / 1000 }]
      });
    }
  }
};
</script>

<style>
* { box-sizing: border-box; }
body { margin: 0; background: #f3f6f8; color: #172b3a; font-family: 'Malgun Gothic', sans-serif; }
main { max-width: 820px; margin: 32px auto; padding: 36px; background: white; border: 1px solid #dce4ea; border-radius: 16px; }
header { border-bottom: 1px solid #dce4ea; margin-bottom: 28px; padding-bottom: 18px; }
h1 { font-size: 1.75rem; } h2 { font-size: 1.3rem; line-height: 1.6; }
p { line-height: 1.9; } .eyebrow { color: #496574; font-size: .85rem; }
label { display: block; font-weight: 600; margin-bottom: 10px; }
input:not([type=radio]), textarea { width: 100%; font: inherit; padding: 14px; border: 1px solid #8096a5; border-radius: 6px; }
textarea { resize: vertical; } fieldset { border: 0; padding: 0; }
.option { padding: 14px; background: #f3f6f8; border-radius: 6px; font-weight: 400; line-height: 1.7; }
button { background: #176776; color: white; border: 1px solid #176776; border-radius: 6px; font: inherit; padding: 12px 18px; cursor: pointer; margin-top: 16px; }
button:disabled { opacity: .55; cursor: not-allowed; } button.secondary { display: block; background: white; color: #176776; }
:focus-visible { outline: 3px solid #e09c33; outline-offset: 3px; }
.notice, footer { color: #526777; font-size: .9rem; line-height: 1.8; }
.reference { margin-top: 18px; padding: 20px; background: #f3f6f8; border-left: 3px solid #176776; }
.error { color: #a22222; padding: 14px; background: #fff0ed; } footer { border-top: 1px solid #dce4ea; margin-top: 36px; padding-top: 16px; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }
@media (max-width: 600px) { main { margin: 0; padding: 22px; border-radius: 0; } button { width: 100%; } }
</style>
